import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/auth-provider';
import { Link } from 'react-router-dom';
import AuthLayout from '@/layouts/auth-layout';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session } = useAuth();

  useEffect(() => {
    if (session?.user) {
      navigate('/');
    }
  }, [session, navigate]);

  const handleTraditionalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setMessage(error.message);
    } else {
      navigate('/');
    }
    setLoading(false);
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const origin = window.location.origin;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: origin + '/verify',
      },
    });
    if (error) {
      setMessage(error.message);
    } else {
      const params = { email, type: 'signin' };
      navigate({
        pathname: '/verify',
        search: new URLSearchParams(params).toString(),
      });
    }
    setLoading(false);
  };

  return (
    <AuthLayout>
      <Tabs defaultValue="traditional" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="traditional">{t('password')}</TabsTrigger>
          <TabsTrigger value="magic">{t('otp')}</TabsTrigger>
        </TabsList>
        <TabsContent value="traditional">
          <form onSubmit={handleTraditionalLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {message && <p className="text-red-500">{message}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('loading') : t('login')}
            </Button>
          </form>
        </TabsContent>
        <TabsContent value="magic">
          <form onSubmit={handleMagicLink} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {message && <p className="text-red-500">{message}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('loading') : t('sendMagicLink')}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
      <div className="mt-4 text-center text-sm">
        <Link to="/forgot-password" className="text-primary hover:underline">
          {t('forgotPassword')}
        </Link>
      </div>
    </AuthLayout>
  );
};
